import { BIFROST_FORMULA_RE } from '../taskpane/formulaParsing';

/**
 * Reading a union written as cells joined by `+`, e.g. `E8+E7+E6+E5`.
 *
 * Excel evaluates such an argument before the function sees it: numbers arrive
 * as their sum (accounts 3000 and 3100 become 6100, a valid-looking account) and
 * text as #VALUE!. The cell's formula still holds what the user wrote, so it is
 * read back and the referenced cells become one union value. Only plain cell
 * references count; anything with a literal (`$B$5+1`) stays the arithmetic it is.
 */

export interface CellRef {
  sheet: string | null;
  cell: string;
}

const SHEET = String.raw`(?:'(?:[^']|'')+'|[A-Za-z0-9_.]+)!`;
const CELL = String.raw`\$?[A-Za-z]{1,3}\$?[0-9]{1,7}`;
const REF = new RegExp(`^(${SHEET})?(${CELL})$`);

function unquoteSheet(sheet: string): string {
  const name = sheet.slice(0, -1);
  return name.startsWith("'") ? name.slice(1, -1).replace(/''/g, "'") : name;
}

function parseRef(text: string): CellRef | null {
  const match = REF.exec(text.trim());
  if (!match) return null;
  return { sheet: match[1] ? unquoteSheet(match[1]) : null, cell: match[2].replace(/\$/g, '') };
}

/** The cells of an argument written as two or more references joined by `+`, or null. */
export function unionReferences(argument: string): CellRef[] | null {
  const parts = argument.split('+');
  if (parts.length < 2) return null;
  const refs: CellRef[] = [];
  for (const part of parts) {
    const ref = parseRef(part);
    if (!ref) return null;
    refs.push(ref);
  }
  return refs;
}

/**
 * The raw argument text of every BIFROST call in a formula, after the formula
 * name. `range.formulas` is always in en-US syntax, so arguments are separated
 * by commas whatever the user's locale.
 */
export function bifrostCallArguments(formula: string): string[][] {
  const calls: string[][] = [];
  const pattern = new RegExp(BIFROST_FORMULA_RE.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(formula)) !== null) {
    const args: string[] = [];
    let depth = 1;
    let inQuote = false;
    let current = '';
    for (let i = match.index + match[0].length; i < formula.length; i++) {
      const ch = formula[i];
      if (ch === '"') inQuote = !inQuote;
      if (!inQuote && ch === '(') depth++;
      if (!inQuote && ch === ')' && --depth === 0) { args.push(current); break; }
      if (!inQuote && ch === ',' && depth === 1) { args.push(current); current = ''; continue; }
      current += ch;
    }
    calls.push(args.slice(1).map(a => a.trim()));
  }
  return calls;
}

export type CellValue = string | number | boolean | null | undefined;

/**
 * Whether what Excel passed for an argument is what it computes from the
 * referenced cells: their sum for numbers, an error for text. This ties the
 * formula that was read to this invocation and this argument.
 */
export function matchesExcelResult(received: unknown, values: CellValue[]): boolean {
  if (isExcelError(received)) return values.some(v => typeof v === 'string' && v.trim() !== '' && Number.isNaN(Number(v)));
  if (typeof received !== 'number') return false;
  const sum = values.reduce<number>((total, v) => total + (typeof v === 'number' ? v : Number(v || 0)), 0);
  return Math.abs(sum - received) < 1e-9;
}

/** One union value from the referenced cells; blanks are left out. */
export function unionValue(values: CellValue[], addOperator: string): string | null {
  const parts = values
    .map(v => (v === null || v === undefined ? '' : String(v).trim()))
    .filter(v => v !== '');
  return parts.length > 0 ? parts.join(addOperator) : null;
}

export function isExcelError(value: unknown): value is { code?: string } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
